<template>
  <section>
    <div class="tabs-wrapper">
      <b-row
        v-if="loading"
        class="mb-2"
      >
        <b-col sm="2">
          <b-skeleton
            animation="wave"
            width="100%"
          />
        </b-col>
        <b-col sm="2">
          <b-skeleton
            animation="wave"
            width="100%"
          />
        </b-col>
        <b-col sm="12">
          <b-skeleton-table
            :rows="10"
            :columns="4"
            :table-props="{ bordered: true, striped: true }"
          />
        </b-col>
      </b-row>
      <b-tabs
        v-else
        :value="activeTabIndex"
        @input="onChangeTab"
      >
        <b-tab
          :title="$tk('ClientPortal.Monetization.Statistics.Placements')"
        >
          <b-card
            class="client-table--properties-details"
            no-body
          >
            <b-card-header class="pt-0 pb-1 pl-1">
              <b-button
                v-if="accountAccessManager.canManageProperties()"
                variant="primary"
                :to="`/${$route.params.accountId}/property/placement?propertyId=${propertyId}`"
                size="sm"
              >
                <GjIcon
                  class="mr-50"
                  name="Plus"
                />
                {{ $tk('ClientPortal.Monetization.Statistics.CreatePlacement') }}
              </b-button>
            </b-card-header>
            <b-table
              :items="wrapper.placementTableList"
              :fields="placementsFields"
              class="client-table__data placement-table"
              :tbody-tr-class="newRecordRowStyle"
              show-empty
              :empty-text="$tk('ClientPortal.Placement.NoPlacementsHaveBeenCreatedYet')"
              :current-page="placementsCurrentPage"
              :per-page="perPage"
              responsive
            >
              <template #empty="scope">
                <div class="text-center p-3 client-table__empty">
                  <div class="empty__client-table">
                    <GjIcon
                      size="64px"
                      name="Widget"
                    />
                    <div class="mb-50">
                      {{ scope.emptyText }}
                    </div>
                    <b-link :to="`/${$route.params.accountId}/property/placement?propertyId=${propertyId}`">
                      <GjIcon
                        size="14"
                        name="Plus"
                      />
                      <span class="create-text">{{ $tk('ClientPortal.Monetization.Statistics.CreatePlacement') }}</span>
                    </b-link>
                  </div>
                </div>
              </template>
              <template #cell(name)="data">
                <b-link :to="`/${$route.params.accountId}/property/placement?propertyId=${propertyId}&placementId=${data.item.id}`">
                  {{ data.item.name }}
                </b-link>
              </template>
              <template #cell(formats)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <span>
                    {{ data.item.formats }}
                  </span>
                  <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none"
                    no-caret
                    button-class="p-0"
                    right
                  >
                    <template v-slot:button-content>
                      <GjIcon name="Moreoptions_fill" />
                    </template>
                    <b-dropdown-item :to="`/${$route.params.accountId}/property/placement?propertyId=${propertyId}&placementId=${data.item.id}&accountId=${$route.params.accountId}`">
                      <GjIcon
                        class="mr-50"
                        name="Edit"
                      />
                      {{ $tk('ClientPortal.Common.Edit') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
            <b-card-footer
              v-if="wrapper.placementTableList.length > 0"
              class="client-table__footer d-flex"
            >
              <b-pagination
                v-model="placementsCurrentPage"
                :total-rows="placementsTotalRows"
                :per-page="perPage"
                size="sm"
                class="my-0 mr-1"
                hide-goto-end-buttons
              >
                <template #prev-text>
                  <GjIcon
                    class="mr-25"
                    name="ArrowLeft"
                  />
                </template>
                <template #next-text>
                  <GjIcon
                    class="ml-25"
                    name="ArrowRight"
                  />
                </template>
              </b-pagination>
              <span>
                {{ $tk('ClientPortal.Pagination.Viewing') }} {{ placementsFromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ placementsToCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ placementsTotalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
              </span>
            </b-card-footer>
          </b-card>
        </b-tab>
        <b-tab :title="$tk('ClientPortal.Property.Targeting.Targeting')">
          <b-card
            class="client-table--properties-details"
            no-body
          >
            <b-card-header class="pt-0 pb-1 pl-1">
              <b-button
                v-if="accountAccessManager.canManageProperties()"
                variant="primary"
                :to="`/${$route.params.accountId}/property/targeting?propertyId=${propertyId}`"
                size="sm"
              >
                <GjIcon
                  class="mr-50"
                  name="Plus"
                />
                {{ $tk('ClientPortal.Property.Targeting.CreateTargetingProperty') }}
              </b-button>
            </b-card-header>
            <b-table
              :items="targetingWrapper.customTargetingProperties"
              :fields="targetingFields"
              :tbody-tr-class="newRecordRowStyle"
              class="targeting-table client-table__data"
              show-empty
              :empty-text="$tk('ClientPortal.Placement.NoTargetingHaveBeenCreatedYet')"
              :current-page="targetingCurrentPage"
              :per-page="perPage"
              responsive
            >
              <template #empty="scope">
                <div class="text-center p-3 client-table__empty">
                  <div class="empty__client-table">
                    <GjIcon
                      size="64px"
                      name="Widget"
                    />
                    <div class="mb-50">
                      {{ scope.emptyText }}
                    </div>
                    <b-link :to="`/${$route.params.accountId}/property/targeting?propertyId=${propertyId}`">
                      <GjIcon
                        size="14"
                        name="Plus"
                      />
                      <span class="create-text">{{ $tk('ClientPortal.Property.Targeting.CreateTargetingProperty') }}</span>
                    </b-link>
                  </div>
                </div>
              </template>
              <template #cell(type)="data">
                {{ getTypeText(data.item.type) }}
              </template>
              <template #cell(matchType)="data">
                <span>
                  {{ getMatchTypeText(data.item.matchType) }}
                </span>
              </template>
              <template #cell(privacyType)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <span>
                    {{ getPrivacyTypeText(data.item.privacyType) }}
                  </span>
                  <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none"
                    no-caret
                    button-class="p-0"
                    right
                  >
                    <template v-slot:button-content>
                      <GjIcon name="Moreoptions_fill" />
                    </template>
                    <b-dropdown-item :to="`/${$route.params.accountId}/property/targeting?propertyId=${propertyId}&targetingPropertyId=${data.item.id}`">
                      <GjIcon
                        class="mr-50"
                        name="Edit"
                      />
                      {{ $tk('ClientPortal.Common.Edit') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="showConfirmTargetingPropertyDeleteModal(data.item, data.index)">
                      <GjIcon
                        class="mr-50"
                        name="Delete"
                      />
                      {{ $tk('ClientPortal.Property.Targeting.DeleteTargetingProperty') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
            <b-card-footer
              v-if="targetingWrapper.customTargetingProperties.length > 0"
              class="client-table__footer d-flex"
            >
              <b-pagination
                v-model="targetingCurrentPage"
                :total-rows="targetingTotalRows"
                :per-page="perPage"
                size="sm"
                class="my-0 mr-1"
                hide-goto-end-buttons
              >
                <template #prev-text>
                  <GjIcon
                    class="mr-25"
                    name="ArrowLeft"
                  />
                </template>
                <template #next-text>
                  <GjIcon
                    class="ml-25"
                    name="ArrowRight"
                  />
                </template>
              </b-pagination>
              <span>
                {{ $tk('ClientPortal.Pagination.Viewing') }} {{ targetingFromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ targetingToCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ targetingTotalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
              </span>
            </b-card-footer>
          </b-card>
        </b-tab>
      </b-tabs>
    </div>
    <b-modal
      id="confirm-targeting-property-delete-modal"
      ref="confirm-targeting-property-delete-modal"
      centered
      :title="$tk('ClientPortal.Property.Targeting.AreYouSureYouWantToDeleteThisKey')"
    >
      <span>{{ $tk('ClientPortal.Property.TargetingName') }}: {{ confirmModal.name }}</span>
      <template #modal-footer="{ cancel, close }">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="saving"
          @click.prevent="cancel()"
        >
          {{ $tk('Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          :disabled="saving"
          @click.prevent="deleteTargetingProperty(confirmModal.targetingPropertyId, confirmModal.index),close()"
        >
          {{ $tk('Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import PropertiesService from '@/api/client/properties.service'
import notification from '@/mixins/toastification'
import TargetingService from '@/api/client/targeting.service'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import store from '@/store'
import { computed, ref } from '@vue/composition-api'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: {},
      saving: false,
      confirmModal: {},
      placementsCurrentPage: 1,
      targetingCurrentPage: 1,
      perPage: 10,
      loading: true,
      propertyId: 0,
      tabHashes: ['#placements', '#targeting'],
      accountAccessManager: AccountAccessManager,
    }
  },
  computed: {
    placementsFromCount() {
      return (this.placementsCurrentPage - 1) * this.perPage + 1
    },
    placementsToCount() {
      const toCount = this.placementsFromCount + this.perPage - 1
      return Math.min(toCount, this.placementsTotalRows)
    },
    targetingFromCount() {
      return (this.targetingCurrentPage - 1) * this.perPage + 1
    },
    targetingToCount() {
      const toCount = this.placementsFromCount + this.perPage - 1
      return Math.min(toCount, this.targetingTotalRows)
    },
    targetingWrapper() {
      return this.wrapper.targetingWrapper
    },
    activeTabIndex() {
      const route = this.$route.hash.toLowerCase()
      return this.tabHashes.findIndex(x => x === route) || 0
    },
    placementsFields() {
      return [
        {
          key: 'id',
          label: this.$tk('ClientPortal.Property.Id'),
        },
        {
          key: 'name',
          label: this.$tk('ClientPortal.Property.Name'),
        },
        {
          key: 'formats',
          label: this.$tk('ClientPortal.Property.Formats'),
        },
      ]
    },
    targetingFields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Property.Targeting.Name'),
        },
        {
          key: 'type',
          label: this.$tk('ClientPortal.Property.Targeting.Type'),
        },
        {
          key: 'matchType',
          label: this.$tk('ClientPortal.Property.Targeting.MatchType'),
        },
        {
          key: 'privacyType',
          label: this.$tk('ClientPortal.Property.Targeting.PrivacyType'),
        },
      ]
    },
    targetingTotalRows() {
      return this.wrapper.targetingWrapper.customTargetingProperties.length
    },
    placementsTotalRows() {
      return this.wrapper.placementTableList.length
    },
  },
  watch: {
    '$route.params.accountId': 'onAccountChange',
  },
  async mounted() {
    this.propertyId = this.$route.query.propertyId !== undefined ? this.$route.query.propertyId : ''
    try {
      const response = await PropertiesService.getProperty(this.propertyId, this.$route.params.accountId)
      this.wrapper = response
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    onAccountChange() {
      this.$router.push(`/${this.$route.params.accountId}/properties`)
    },
    showConfirmTargetingPropertyDeleteModal(targetingProperty, index) {
      this.confirmModal = { name: targetingProperty.name, targetingPropertyId: targetingProperty.id, index }
      this.$refs['confirm-targeting-property-delete-modal'].show()
    },
    hideConfirmTargetingPropertyDeleteModal() {
      this.$refs['confirm-targeting-property-delete-modal'].hide()
    },
    getTypeText(code) {
      const type = this.targetingWrapper.customTargetingPropertyTypes.find(x => x.value === code)

      if (type) return type.text

      return ''
    },
    getMatchTypeText(code) {
      const matchType = this.targetingWrapper.customTargetingPropertyMatchTypes.find(x => x.value === code)

      if (matchType) return matchType.text

      return ''
    },
    async deleteTargetingProperty(targetingPropertyId, index) {
      this.saving = true
      try {
        const response = await TargetingService.deleteCustomTargetingProperty(this.propertyId, targetingPropertyId, this.$route.params.accountId)
        if (response.saved) {
          this.targetingWrapper.customTargetingProperties.splice(index, 1)
          this.success(response.message)
        } else {
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.saving = false
        this.hideConfirmTargetingPropertyDeleteModal()
      }
    },
    onChangeTab(tab) {
      const hash = this.tabHashes[tab]
      store.commit('app/TOGGLE_TABLE', tab)
      this.$router.replace({ query: this.$route.query, hash })
    },
    getPrivacyTypeText(code) {
      const matchType = this.targetingWrapper.customTargetingPropertyPrivacyTypes.find(x => x.value === code)
      if (matchType) return matchType.text
      return ''
    },
  },
  setup() {
    const isCreatedNew = computed(() => store.state.app.isCreatedNew)
    const typeOfTable = computed(() => store.state.app.typeOfTable)
    const alreadySet = ref(false)
    const firstRow = ref(null)

    const newRecordRowStyle = () => {
      if (!alreadySet.value) {
        firstRow.value = Array.from(document.querySelectorAll('table > tbody > tr:first-child')).pop()
        if (typeOfTable.value === 0) {
          firstRow.value = Array.from(document.querySelectorAll('.placement-table > .table > tbody > tr:first-child')).pop()
        } else if (typeOfTable.value === 1) {
          firstRow.value = Array.from(document.querySelectorAll('.targeting-table > table > tbody > tr:first-child')).pop()
        }
        if (firstRow.value) {
          if (isCreatedNew.value) {
            firstRow.value.classList.add('table-row-animation')
          } else if (firstRow.value.classList.contains('table-row-animation')) {
            firstRow.value.classList.remove('table-row-animation')
          }
          alreadySet.value = true
        }
      }
      return ''
    }
    if (isCreatedNew.value) {
      setTimeout(() => {
        store.commit('app/TOGGLE_NEW_CREATE_STATE', false)
        firstRow.value.classList.remove('table-row-animation')
      }, 3000)
    }
    return {
      isCreatedNew,
      newRecordRowStyle,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
  .tabs-wrapper {
    background-color: #ffffff;
    height: 20vh;
    border-radius: 4px;
  }
  .tab-border {
    box-shadow: 10px 5px 5px red;
  }
</style>
